const statusCard = (title, rows, extra = {}) => ({
  kind: 'teamStatusCard',
  title,
  rows,
  ...extra,
});

const listCard = (title, rows, extra = {}) => ({
  kind: 'teamListCard',
  title,
  rows,
  ...extra,
});

const eventListCard = (title, rows, extra = {}) => ({
  kind: 'eventListCard',
  title,
  rows,
  ...extra,
});

const dashboardCard = (title, rows, extra = {}) => ({
  kind: 'dashboardCard',
  title,
  rows,
  ...extra,
});

const notificationsCard = (title, rows, extra = {}) => ({
  kind: 'notificationsCard',
  title,
  rows,
  ...extra,
});

const joinFormCard = (title, questions, extra = {}) => ({
  kind: 'joinFormCard',
  title,
  questions,
  ...extra,
});

const documentsCard = (title, rows, extra = {}) => ({
  kind: 'documentsCard',
  title,
  rows,
  ...extra,
});

const publicLinksCard = (title, rows, extra = {}) => ({
  kind: 'publicLinksCard',
  title,
  rows,
  ...extra,
});

const brandingCard = (title, rows, extra = {}) => ({
  kind: 'brandingCard',
  title,
  rows,
  ...extra,
});

const profileFieldsCard = (title, rows, extra = {}) => ({
  kind: 'profileFieldsCard',
  title,
  rows,
  ...extra,
});

const paymentCard = (title, rows, extra = {}) => ({
  kind: 'paymentCard',
  title,
  rows,
  ...extra,
});

const messageThreadCard = (title, data, extra = {}) => ({
  kind: 'messageThreadCard',
  title,
  ...data,
  ...extra,
});

const inboxBroadcastCard = (title, rows, extra = {}) => ({
  kind: 'inboxBroadcastCard',
  title,
  rows,
  ...extra,
});

const emailCampaignsCard = (title, rows, extra = {}) => ({
  kind: 'emailCampaignsCard',
  title,
  rows,
  ...extra,
});

const boardOverviewCard = (title, rows, extra = {}) => ({
  kind: 'boardOverviewCard',
  title,
  rows,
  ...extra,
});

const boardThreadCard = (title, data, extra = {}) => ({
  kind: 'boardThreadCard',
  title,
  ...data,
  ...extra,
});

const dutyRosterCard = (title, data, extra = {}) => ({
  kind: 'dutyRosterCard',
  title,
  ...data,
  ...extra,
});

const mooringMapCard = (title, data, extra = {}) => ({
  kind: 'mooringMapCard',
  title,
  ...data,
  ...extra,
});

const featureFamilies = [
  {
    key: 'general',
    label: 'General Features',
    description:
      'Core club modules, communications, and the member-facing surfaces they feed into.',
    groups: [
      {
        key: 'running',
        title: 'Running Your Club',
        description: 'The core modules used to configure the club and manage members.',
        modules: [
          {
            key: 'events',
            label: 'Events',
            description:
              'Create team events, manage attendance, and surface them across team and personal calendars.',
            highlights: [
              'Event setup and role planning',
              'Team event list and calendar access',
              'Personal and family calendar mirrors',
              'Event notifications when things change',
            ],
            stages: [
              {
                key: 'events-setup',
                title: 'Set up and manage events',
                description:
                  'Admins create events, define locations or online links, and manage the event setup from the team card.',
                cards: [
                  statusCard('Events', [
                    { icon: '📅', label: 'Upcoming', value: '5', tone: 'available' },
                    { icon: '🪑', label: 'Open roles', value: '12', tone: 'pending' },
                  ], {
                    paragraph:
                      'The Events card is where clubs keep upcoming activity visible and jump into the calendar or setup screens.',
                    actions: ['Open Calendar', 'Manage event setup'],
                  }),
                  eventListCard('Events', [
                    { title: 'Spring Learn to Row Taster', meta: 'Wed 24 Apr 2026 • 18:30 • Boathouse' },
                    { title: 'Captain’s Welcome Evening', meta: 'Fri 26 Apr 2026 • 19:00 • Clubhouse' },
                    { title: 'Sunday Squad Session', meta: 'Sun 28 Apr 2026 • 08:00 • River' },
                  ]),
                ],
              },
              {
                key: 'events-calendar',
                title: 'Events appear in personal calendars',
                description:
                  'The same events can be mirrored into personal and family calendars so members see what applies to them.',
                cards: [
                  dashboardCard('Your Upcoming Events', [
                    { title: 'Sunday Squad Session', meta: '28/04/2026 @ 08:00 • River' },
                    { title: 'Captain’s Welcome Evening', meta: '26/04/2026 @ 19:00 • Clubhouse' },
                  ], {
                    buttonLabel: 'View Full Calendar',
                  }),
                  dashboardCard('Family Events', [
                    { title: 'Junior Training', meta: '27/04/2026 @ 10:00 • Boathouse (Brown Family)' },
                    { title: 'Awards Night', meta: '30/04/2026 @ 18:30 • Clubhouse (Brown Family)' },
                  ], {
                    buttonLabel: 'View Brown Family Calendar',
                  }),
                ],
              },
              {
                key: 'events-notifications',
                title: 'Easily update with notifications',
                description:
                  'When event details change or members are prompted to act, those updates surface as notifications that link back into the relevant flow.',
                cards: [
                  notificationsCard('Recent Notifications', [
                    { message: 'Captain’s Welcome Evening has moved to 19:30.', meta: 'Today at 11:42' },
                    { message: 'You have been added to Sunday Squad Session.', meta: 'Today at 09:10' },
                    { message: 'Spring Learn to Row Taster is now open for RSVP.', meta: 'Yesterday at 17:24' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'publicLinks',
            label: 'Public Links',
            description:
              'Publish useful links for prospective members and existing members from a single club-managed list.',
            highlights: [
              'Admin-managed public link list',
              'Visible on the club-facing surfaces',
            ],
            stages: [
              {
                key: 'links-setup',
                title: 'Manage the club’s public links',
                description:
                  'Admins keep social links, websites, and key club destinations in one card, and that same list is what members and prospects see later.',
                cards: [
                  publicLinksCard('Public Links', [
                    { icon: '🌐', label: 'Club Website', url: 'membernest.club' },
                    { icon: '💬', label: 'WhatsApp Group', url: 'chat.whatsapp.com/club' },
                    { icon: '📸', label: 'Instagram', url: 'instagram.com/membernestclub' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'documents',
            label: 'Documents',
            description:
              'Store club documents, mark them public or members-only, and require them during join where needed.',
            highlights: [
              'Document management',
              'Required-on-join files',
              'Public and members-only visibility',
            ],
            stages: [
              {
                key: 'documents-admin',
                title: 'Upload and organise club documents',
                description:
                  'Documents live in their own team card with visibility and join requirement settings, so the same files can be public, members-only, or required during join.',
                cards: [
                  documentsCard('Documents', [
                    { title: 'Club Handbook', meta: 'Updated for 2026 season', badge: 'Members only' },
                    { title: 'Safety Declaration', meta: 'Signed during join flow', badge: 'Required on join' },
                    { title: 'Welcome Pack', meta: 'Shared publicly for prospects', badge: 'Public' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'branding',
            label: 'Branding',
            description:
              'Set club colours and brand assets so public and internal surfaces reflect the club properly.',
            highlights: [
              'Brand colour configuration',
              'Logo support',
              'Visual carry-through into club-facing pages',
            ],
            stages: [
              {
                key: 'branding-settings',
                title: 'Set the club’s brand colours and assets',
                description:
                  'Branding is configured in its own card and then carries through into the club-facing experience instead of being managed separately.',
                cards: [
                  brandingCard('Branding', [
                    { label: 'Primary', value: '#0D5F8C' },
                    { label: 'Secondary', value: '#F7F9FC' },
                    { label: 'Heading Text', value: '#FFFFFF' },
                    { label: 'Body Text', value: '#1F2937' },
                  ], {
                    logoLabel: 'River City RC',
                  }),
                ],
              },
            ],
          },
          {
            key: 'profileFields',
            label: 'Profile Fields',
            description:
              'Define club-specific member data, collect it during join, and keep it editable later.',
            highlights: [
              'Admin-configured custom fields',
              'Join-form capture',
              'Ongoing member record updates',
            ],
            stages: [
              {
                key: 'profile-config',
                title: 'Configure the fields the club needs',
                description:
                  'Clubs decide which profile details matter and can request updates from members later.',
                cards: [
                  statusCard('Profile Details', [
                    { icon: '📝', label: 'Configured fields', value: '4', tone: 'available' },
                    { icon: '⚠️', label: 'Required fields', value: '2', tone: 'pending' },
                  ], {
                    paragraph:
                      'Fields can be optional or required, and admins can request members to confirm or update their details.',
                    actions: ['Request Updates', 'Profile fields'],
                  }),
                ],
              },
              {
                key: 'profile-join',
                title: 'Capture those answers during join',
                description:
                  'Profile fields can appear inside the join journey alongside the standard membership questions.',
                cards: [
                  joinFormCard('Join Form', [
                    'British Rowing membership number',
                    'Emergency contact name',
                    'Emergency contact phone',
                    'Medical notes',
                  ], {
                    enabledLabel: 'These fields are shown during join when the club requires them.',
                  }),
                ],
              },
              {
                key: 'profile-record',
                title: 'Keep them visible on the member record',
                description:
                  'Once collected, the details continue to live on the team-side profile record rather than disappearing after join.',
                cards: [
                  profileFieldsCard('Profile Details', [
                    { label: 'British Rowing membership number', value: 'BR-42188' },
                    { label: 'Emergency contact name', value: 'Jo Brown' },
                    { label: 'Emergency contact phone', value: '07700 900123' },
                    { label: 'Medical notes', value: 'Asthma inhaler carried' },
                  ], {
                    summary: '4 of 4 fields completed, 2 required.',
                  }),
                ],
              },
            ],
          },
          {
            key: 'boltOns',
            label: 'Bolt Ons',
            description:
              'Offer extra paid items after join, such as storage, coaching, or other recurring extras.',
            highlights: [
              'Admin-managed add-on catalogue',
              'Member purchase and management',
              'Ongoing visibility after purchase',
            ],
            stages: [
              {
                key: 'boltons-admin',
                title: 'Create the bolt-on catalogue',
                description:
                  'Bolt-ons are configured from their own team card, including price and availability.',
                cards: [
                  listCard('Bolt-ons', [
                    { title: 'Boat Storage', meta: '£120 yearly • 18 active', badge: 'Live' },
                    { title: 'Gym Access', meta: '£18 monthly • 26 active', badge: 'Live' },
                    { title: 'Performance Squad', meta: '£35 monthly • Invite only', badge: 'Limited' },
                  ]),
                ],
              },
              {
                key: 'boltons-member',
                title: 'Members then see and manage what they can buy',
                description:
                  'The same catalogue becomes the member-facing purchase and management surface after join.',
                cards: [
                  listCard('Bolt-ons', [
                    { title: 'Boat Storage', meta: 'Manage existing purchase • renews yearly', badge: 'Purchased' },
                    { title: 'Gym Access', meta: '£18 monthly • available to buy', badge: 'Available' },
                    { title: 'Performance Squad', meta: 'Contact captain to be added', badge: 'Restricted' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'discounts',
            label: 'Discounts',
            description:
              'Create membership discounts, apply them during payment, and review redemptions afterwards.',
            highlights: [
              'Discount rule management',
              'Checkout application',
              'Redemption history',
            ],
            stages: [
              {
                key: 'discounts-admin',
                title: 'Configure and review membership discounts',
                description:
                  'Discounts have their own team card with active rules and recent redemptions.',
                cards: [
                  statusCard('Membership Discounts', [
                    { icon: '🏷️', label: 'Active rules', value: '3', tone: 'available' },
                    { icon: '✅', label: 'Total redemptions', value: '17', tone: 'available' },
                  ]),
                  listCard('Recent usage', [
                    { title: 'Alex Turner', meta: 'EARLYBIRD • £20.00 • manual approval' },
                    { title: 'Sam Patel', meta: 'COXING • £12.00 • personal offer' },
                    { title: 'Mia Brown', meta: 'RETURNER • £15.00 • code' },
                  ]),
                ],
              },
              {
                key: 'discounts-checkout',
                title: 'Apply the discount during checkout',
                description:
                  'When a valid code or offer applies, the payment breakdown shows it directly in the same checkout flow.',
                cards: [
                  paymentCard('Complete Your Subscription', [
                    { label: 'Base membership', value: '£120.00' },
                    { label: 'Membership discount', value: '-£20.00', emphasis: 'positive' },
                    { label: 'Platform fee', value: '£1.00' },
                    { label: 'Stripe fee', value: '£3.04' },
                    { label: 'Total', value: '£104.54', total: true },
                  ], {
                    note: 'Valid discounts change the amount before the member confirms payment.',
                  }),
                ],
              },
              {
                key: 'discounts-reporting',
                title: 'Track redemptions afterwards',
                description:
                  'Recent uses stay visible so the club can see who used what and when.',
                cards: [
                  listCard('Recent Discount Redemptions', [
                    { title: 'Alex Turner', meta: 'EARLYBIRD • £20.00 • 22 Apr 2026 10:11' },
                    { title: 'Sam Patel', meta: 'COXING • £12.00 • 20 Apr 2026 08:42' },
                    { title: 'Mia Brown', meta: 'RETURNER • £15.00 • 18 Apr 2026 17:05' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'waitlist',
            label: 'Waitlist',
            description:
              'Hold members in order when spaces are full, then release places into the normal payment flow when ready.',
            highlights: [
              'Waitlist entry during join',
              'Admin waitlist management',
              'Release and follow-on payment journey',
            ],
            stages: [
              {
                key: 'waitlist-entry',
                title: 'Joiners can be placed onto a waitlist',
                description:
                  'If the club is full, the join flow can route people into a waitlist rather than taking payment immediately.',
                cards: [
                  statusCard('Waitlist', [
                    { icon: '⏳', label: 'Mode', value: 'Enabled', tone: 'pending' },
                    { icon: '👥', label: 'Current queue', value: '6', tone: 'pending' },
                  ], {
                    paragraph:
                      'The waitlist stage is used when capacity or approval rules mean a member should wait before being offered a place.',
                  }),
                ],
              },
              {
                key: 'waitlist-admin',
                title: 'Admins manage the queue from the waitlist card',
                description:
                  'The team-side waitlist card keeps the order and the key join details visible in one place.',
                cards: [
                  listCard('Waitlist (6)', [
                    { title: '#1 Lucy Green', meta: 'Individual request • Waitlisted 18 Apr 2026, 14:05' },
                    { title: '#2 Brown Family', meta: 'Family request (3 members) • Waitlisted 19 Apr 2026, 09:12' },
                    { title: '#3 Ben Walsh', meta: 'Individual request • Waitlisted 20 Apr 2026, 08:41' },
                  ]),
                ],
              },
              {
                key: 'waitlist-release',
                title: 'Released places move into the normal payment path',
                description:
                  'When a place is released, the member is notified and can continue to the same payment flow used elsewhere.',
                cards: [
                  notificationsCard('Recent Notifications', [
                    { message: 'A membership place is now available for you at River City RC.', meta: 'Today at 08:20' },
                  ]),
                  paymentCard('Complete Your Subscription', [
                    { label: 'Individual membership', value: '£120.00' },
                    { label: 'Platform fee', value: '£1.00' },
                    { label: 'Stripe fee', value: '£3.04' },
                    { label: 'Total', value: '£124.54', total: true },
                  ]),
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'communication',
        title: 'Communicating With Members',
        description: 'Direct messaging, in-app broadcasts, and tracked email.',
        modules: [
          {
            key: 'messaging',
            label: 'Messaging',
            description:
              'Members and admins can start direct conversations and then continue them inside the platform.',
            highlights: [
              'Start direct messages',
              'Persistent message threads',
              'Unread message notifications',
            ],
            stages: [
              {
                key: 'messages-start',
                title: 'Start a direct message',
                description:
                  'Messaging begins from the people already inside the club workflow rather than a separate standalone tool, and the same action drops into a normal message thread straight away.',
                cards: [
                  listCard('Members', [
                    { title: 'Alex Turner', meta: 'Senior squad • last active today', badge: 'Message' },
                    { title: 'Lucy Green', meta: 'Learn to Row • awaiting course start', badge: 'Message' },
                    { title: 'Sam Patel', meta: 'Cox • admin view', badge: 'Message' },
                  ])
                ],
              },
              {
                key: 'messages-thread',
                title: 'Continue the conversation in the message thread',
                description:
                  'Once started, the conversation lives in the same threaded messaging UI members use day to day.',
                cards: [
                  messageThreadCard('Messages', {
                    sidebarItems: [
                      { title: 'Alex Turner', meta: 'Can you make Sunday 8am?', active: true },
                      { title: 'Lucy Green', meta: 'Thanks for the welcome email' },
                      { title: 'Sam Patel', meta: 'Crew plan for Saturday' },
                    ],
                    threadTitle: 'Alex Turner',
                    messages: [
                      { author: 'them', text: 'Can you make Sunday 8am outing?' },
                      { author: 'me', text: 'Yes, I can do that.' },
                      { author: 'them', text: 'Great, I will add you now.' },
                    ],
                    inputPlaceholder: 'Write a message…',
                  }),
                ],
              },
              {
                key: 'messages-notifications',
                title: 'Unread messages also appear as notifications',
                description:
                  'Message notifications link straight back into the specific thread so the member lands in the right conversation.',
                cards: [
                  notificationsCard('Recent Notifications', [
                    { message: 'New message from Alex Turner', meta: 'Today at 12:01' },
                    { message: 'New message from River City RC support', meta: 'Yesterday at 16:19' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'messageBoards',
            label: 'Message Boards',
            description:
              'Create club discussion boards with topics, replies, reactions, event comments, media, and moderation tools.',
            highlights: [
              'Board list with topic and reply activity',
              'Rich topics, replies, reactions, GIFs, and media',
              'Event comments powered by the same boards system',
              'Admin moderation and review queue',
            ],
            stages: [
              {
                key: 'boards-overview',
                title: 'Members choose from the boards they can access',
                description:
                  'The team card gives members a quick route into recent discussion boards and gives admins a review shortcut when something needs attention.',
                cards: [
                  boardOverviewCard('Message Boards', [
                    { title: 'Harbour Notices', meta: '8 topics • 23 replies • last activity today', badge: 'Open' },
                    { title: 'Crew Swaps', meta: '14 topics • 41 replies • last activity 1 hour ago', badge: 'Members' },
                    { title: 'Junior Section', meta: '5 topics • 12 replies • last activity yesterday', badge: 'Tag limited' },
                  ], {
                    summary: '3 accessible boards • 2 open review cases',
                    actionLabel: 'Open Boards',
                    reviewCases: [
                      { title: 'Crew Swaps', meta: 'Report • tone check requested', badge: 'Review' },
                      { title: 'Harbour Notices', meta: 'Auto flag • link needs checking', badge: 'Review' },
                    ],
                  }),
                ],
              },
              {
                key: 'boards-thread',
                title: 'Topics become shared threaded conversations',
                description:
                  'Members can follow the topic, reply in context, and react without turning every update into a direct message.',
                cards: [
                  boardThreadCard('Crew Swaps', {
                    topicTitle: 'Looking for crew on Saturday morning',
                    topicMeta: 'Posted by Maya Collins • today @ 09:14',
                    body:
                      'I have a club boat booked for 09:00 and need one more experienced crew member.',
                    attachments: ['Photo', 'GIF'],
                    reactions: [
                      { emoji: '👍', count: 6, active: true },
                      { emoji: '🙌', count: 3 },
                      { emoji: '👀', count: 2 },
                    ],
                    replies: [
                      { author: 'Alex Turner', text: 'I can make that slot if you still need someone.', meta: 'today @ 09:22' },
                      { author: 'Maya Collins', text: 'Perfect, I will update the crew list now.', meta: 'today @ 09:24' },
                    ],
                  }),
                ],
              },
              {
                key: 'boards-event-comments',
                title: 'Event comments use the same moderation and reaction tools',
                description:
                  'When comments are enabled on an event, the discussion is stored as a board-backed thread and stays connected to the event details.',
                cards: [
                  boardThreadCard('Event Comments', {
                    topicTitle: 'Saturday Harbour Clean-up',
                    topicMeta: 'Event comments • Sat 26 Apr 2026 @ 10:00',
                    body:
                      'Please add any kit requests or arrival notes here so the organisers can see them before the event.',
                    reactions: [
                      { emoji: '👍', count: 9 },
                      { emoji: '❤️', count: 4 },
                    ],
                    replies: [
                      { author: 'Sam Patel', text: 'I can bring spare gloves and bags.', meta: 'yesterday @ 18:12' },
                      { author: 'Priya Shah', text: 'Could someone confirm if juniors should meet by the clubhouse?', meta: 'yesterday @ 18:40' },
                    ],
                    footer: 'Comments use the same board moderation tools.',
                  }),
                  eventListCard('Events', [
                    { title: 'Saturday Harbour Clean-up', meta: 'Sat 26 Apr 2026 • Comments enabled' },
                    { title: 'Junior Safety Briefing', meta: 'Sun 27 Apr 2026 • Comments enabled' },
                  ]),
                ],
              },
              {
                key: 'boards-moderation',
                title: 'Admins can review reports and keep the club space tidy',
                description:
                  'Reported or hidden content stays available as moderation evidence while admins decide whether to restore, keep hidden, or delete it.',
                cards: [
                  boardOverviewCard('Review Queue', [
                    { title: 'Crew Swaps', meta: 'Member report • needs club review', badge: 'Open' },
                    { title: 'Event Comments', meta: 'Hidden reply • organiser review', badge: 'Open' },
                    { title: 'Harbour Notices', meta: 'Auto flag • external link', badge: 'Open' },
                  ], {
                    summary: '3 open cases • 1 resolved today',
                    actionLabel: 'Review Queue',
                    footer: 'Admins can review evidence, resolve cases, and open a private admin thread when support is needed.',
                  }),
                ],
              },
            ],
          },
          {
            key: 'inboxBroadcasts',
            label: 'Inbox Broadcasts',
            description:
              'Send one in-app message to many members, then let each recipient continue privately in their own thread.',
            highlights: [
              'Compose and send to an audience',
              'Broadcast list and delivery status',
              'Replies continue in direct threads',
              'Message notifications on delivery',
            ],
            stages: [
              {
                key: 'broadcast-compose',
                title: 'Compose a broadcast to a selected audience',
                description:
                  'Admins can send one inbox message to many members while still keeping replies separate.',
                cards: [
                  statusCard('Inbox Broadcasts', [
                    { icon: '👥', label: 'Audience', value: '42 recipients', tone: 'available' },
                    { icon: '🏷️', label: 'Filter', value: 'Senior Squad', tone: 'pending' },
                  ], {
                    paragraph:
                      'This is for one-to-many in-app communication without turning it into a noisy shared thread.',
                    actions: ['New Inbox Message'],
                  }),
                ],
              },
              {
                key: 'broadcast-list',
                title: 'Broadcasts stay visible on the team card',
                description:
                  'Admins can review what was sent, when, and to how many people from the same module card.',
                cards: [
                  inboxBroadcastCard('Inbox Broadcasts', [
                    { title: 'From Captain', meta: '22 Apr 2026, 09:10', audience: '42 recipients', status: '42 delivered' },
                    { title: 'From Membership Secretary', meta: '20 Apr 2026, 14:25', audience: '18 recipients', status: '18 delivered' },
                    { title: 'From Learn to Row Lead', meta: '18 Apr 2026, 08:40', audience: '24 recipients', status: '24 delivered' },
                  ], {
                    summary: '3 broadcasts • 84 total recipients',
                    actionLabel: 'New Inbox Message',
                    footer: 'Open a broadcast to view the message and delivery details.',
                  }),
                ],
              },
              {
                key: 'broadcast-thread',
                title: 'Recipients continue in their own private thread',
                description:
                  'Each recipient gets a normal direct thread, so replies do not clutter the original broadcast.',
                cards: [
                  messageThreadCard('Messages', {
                    sidebarItems: [
                      { title: 'Captain', meta: 'Thanks, I can make it', active: true, broadcast: true },
                      { title: 'Captain', meta: 'Sorry, I am away', broadcast: true },
                    ],
                    threadTitle: 'Captain',
                    threadBadge: 'Broadcast',
                    messages: [
                      { author: 'them', text: 'Please confirm if you are available for Saturday.', badge: 'Broadcast' },
                      { author: 'me', text: 'Yes, I am available.' },
                    ],
                    inputPlaceholder: 'Reply to this message…',
                  }),
                  notificationsCard('Recent Notifications', [
                    { message: 'New message from Captain', meta: 'Today at 09:13' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'emailMembers',
            label: 'Email Members',
            description:
              'Send tracked emails to members, then review delivery and engagement in the email list.',
            highlights: [
              'Audience-based email sending',
              'Email history',
              'Open and click metrics',
            ],
            stages: [
              {
                key: 'email-compose',
                title: 'Compose and send email to members',
                description:
                  'Email sending stays inside the team card flow, with date filters and the email list alongside it.',
                cards: [
                  statusCard('Emails', [
                    { icon: '📬', label: 'Delivered this week', value: '162', tone: 'available' },
                    { icon: '👁️', label: 'Open rate', value: '61%', tone: 'available' },
                  ], {
                    paragraph:
                      'Admins can start a new email and then monitor it from the same module card.',
                    actions: ['New Email'],
                  }),
                ],
              },
              {
                key: 'email-campaigns',
                title: 'Emails stay listed with delivery detail',
                description:
                  'Each sent email remains visible with sent time, delivery counts, and drill-in access.',
                cards: [
                  emailCampaignsCard('Emails', [
                    { title: 'April membership reminder', meta: '22 Apr 2026, 08:00 • sent', delivered: '📬 86', opened: '👁️ 54 (63%)', clicked: '🔗 18', bounced: '📨 1' },
                    { title: 'Weekend fixtures update', meta: '20 Apr 2026, 19:30 • sent', delivered: '📬 42', opened: '👁️ 24 (57%)', clicked: '🔗 11', bounced: '📨 0' },
                    { title: 'Learn to Row follow-up', meta: '18 Apr 2026, 12:05 • sent', delivered: '📬 34', opened: '👁️ 25 (74%)', clicked: '🔗 9', bounced: '📨 0' },
                  ], {
                    summary: '3 emails • 162 delivered • 61% open rate',
                  }),
                ],
              },
              {
                key: 'email-metrics',
                title: 'Engagement metrics remain available afterwards',
                description:
                  'Clubs can compare delivery, opens, clicks, and bounces across emails rather than relying on separate tooling.',
                cards: [
                  emailCampaignsCard('Recent Email Metrics', [
                    { title: 'April membership reminder', meta: 'Best open rate this week', delivered: '📬 86', opened: '👁️ 63%', clicked: '🔗 18', bounced: '📨 1' },
                    { title: 'Weekend fixtures update', meta: 'Most link clicks', delivered: '📬 42', opened: '👁️ 57%', clicked: '🔗 11', bounced: '📨 0' },
                  ], {
                    summary: 'Open rates are directional and may be affected by privacy protections.',
                  }),
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    key: 'rowing',
    label: 'Rowing Features',
    description:
      'Rowing-specific modules layered on top of events, payments, messaging, and member records.',
    groups: [
      {
        key: 'rowing',
        title: 'Rowing Clubs',
        description: 'Specialist workflows for rowing operations.',
        modules: [
          {
            key: 'boatBooking',
            label: 'Boat Booking',
            description:
              'Collect boat requests, review allocation state, and notify members when bookings are allocated.',
            highlights: [
              'Member request flow',
              'Admin dashboard and calendar access',
              'Allocation outcomes',
              'Boat allocation notifications',
            ],
            stages: [
              {
                key: 'boats-request',
                title: 'Members request a boat for a future session',
                description:
                  'The module starts with member requests rather than manual admin spreadsheets.',
                cards: [
                  statusCard('Boat Booking', [
                    { icon: '📅', label: 'Allocation', value: 'Mon', tone: 'available' },
                    { icon: '🚣', label: 'Active boats', value: '14', tone: 'available' },
                  ], {
                    paragraph:
                      'Members request a future slot and then wait for the allocation run to confirm the booking.',
                    actions: ['Request a Boat', 'Calendar'],
                  }),
                  listCard('Your upcoming', [
                    { title: 'Sat 26 Apr • 07:30-09:00', meta: 'Prefers 2x • status pending', badge: 'Pending' },
                    { title: 'Sun 27 Apr • 08:00-10:00', meta: 'Prefers single scull', badge: 'Pending' },
                  ]),
                ],
              },
              {
                key: 'boats-admin',
                title: 'Admins review the booking state from the team card',
                description:
                  'Boat Booking keeps the allocation day, active fleet, and request state visible from the main module card.',
                cards: [
                  statusCard('Boat Booking', [
                    { icon: '📅', label: 'Allocation', value: 'Mon', tone: 'available' },
                    { icon: '🚣', label: 'Active boats', value: '14', tone: 'available' },
                  ], {
                    actions: ['Calendar', 'Dashboard'],
                  }),
                ],
              },
              {
                key: 'boats-outcome',
                title: 'Allocated and unfilled outcomes stay visible',
                description:
                  'After allocation, the request state changes from pending to booked or unfilled.',
                cards: [
                  listCard('Allocation outcome', [
                    { title: 'Sat 26 Apr • Emma Walsh', meta: 'Resolute 2x allocated', badge: 'Booked' },
                    { title: 'Sat 26 Apr • Dan Foster', meta: 'No suitable boat free', badge: 'Unfilled' },
                    { title: 'Sun 27 Apr • Zoe Clark', meta: 'Swift single allocated', badge: 'Booked' },
                  ]),
                ],
              },
              {
                key: 'boats-notifications',
                title: 'Members are notified when allocation runs complete',
                description:
                  'Allocation updates can surface as notifications so rowers do not need to keep checking manually.',
                cards: [
                  notificationsCard('Recent Notifications', [
                    { message: 'Your boat request for Sat 26 Apr has been allocated to Resolute 2x.', meta: 'Today at 06:12' },
                    { message: 'Your request for Sun 27 Apr could not be filled this week.', meta: 'Today at 06:11' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'outings',
            label: 'Outings',
            description:
              'Build outings, assign crews and boats, notify rowers, and surface the result alongside event schedules.',
            highlights: [
              'Outing drafts and submissions',
              'Crew assignment and boat allocation',
              'Notification flow for assigned rowers',
              'Event and calendar linkage',
            ],
            stages: [
              {
                key: 'outings-draft',
                title: 'Draft and submit outings',
                description:
                  'Outings begin as planned sessions with draft and submitted states tracked in the module card.',
                cards: [
                  statusCard('Outings', [
                    { icon: '🧭', label: 'Drafts', value: '2', tone: 'pending' },
                    { icon: '🚣', label: 'Submitted', value: '4', tone: 'available' },
                    { icon: '⚠️', label: 'No boat', value: '1', tone: 'unavailable' },
                  ], {
                    actions: ['New outing'],
                  }),
                  listCard('Upcoming outings', [
                    { title: 'Saturday squad outing', meta: 'Sat 26 Apr • 3 crews • submitted', badge: 'Submitted' },
                    { title: 'Sunday novice outing', meta: 'Sun 27 Apr • 2 crews • draft', badge: 'Draft' },
                  ]),
                ],
              },
              {
                key: 'outings-crews',
                title: 'Crew assignment and boat outcomes stay attached to the outing',
                description:
                  'Once crews are assigned and boat allocation runs, the outing still acts as the operational home for that session.',
                cards: [
                  listCard('Crew outcomes', [
                    { title: 'Saturday squad outing', meta: 'Crew A • Resolute 2x • allocated', badge: 'Booked' },
                    { title: 'Saturday squad outing', meta: 'Crew B • No suitable boat yet', badge: 'No boat' },
                    { title: 'Sunday novice outing', meta: 'Crew C • Waiting to submit', badge: 'Draft' },
                  ]),
                ],
              },
              {
                key: 'outings-notify',
                title: 'Assigned rowers can be notified',
                description:
                  'Once the outing becomes actionable for members, the system can surface that in notifications.',
                cards: [
                  notificationsCard('Recent Notifications', [
                    { message: 'You have been assigned to Saturday squad outing.', meta: 'Today at 07:05' },
                    { message: 'Boat allocation for Sunday novice outing is still pending.', meta: 'Yesterday at 18:34' },
                  ]),
                ],
              },
              {
                key: 'outings-calendar',
                title: 'Outings can also show up in the broader event/calendar flow',
                description:
                  'Where the outing is tied into the events module, it surfaces as part of the normal event and calendar journey as well.',
                cards: [
                  eventListCard('Events', [
                    { title: 'Saturday squad outing', meta: 'Sat 26 Apr 2026 • 07:30 • River' },
                    { title: 'Sunday novice outing', meta: 'Sun 27 Apr 2026 • 08:00 • River' },
                  ]),
                  dashboardCard('Your Upcoming Events', [
                    { title: 'Saturday squad outing', meta: '26/04/2026 @ 07:30 • River' },
                  ], {
                    buttonLabel: 'View Full Calendar',
                  }),
                ],
              },
            ],
          },
          {
            key: 'learnToRow',
            label: 'Learn to Row',
            description:
              'Run beginner courses with capacities, waitlists, and generated sessions that flow into the normal event and payment journeys.',
            highlights: [
              'Course setup and capacity management',
              'Join/signup surface',
              'Waitlist and release handling',
              'Admin course management and event linkage',
            ],
            stages: [
              {
                key: 'ltr-setup',
                title: 'Create and manage courses',
                description:
                  'Learn to Row starts with course setup, capacity, waitlist, and fee handling in its own card.',
                cards: [
                  statusCard('Learn to Row', [
                    { icon: '🚣', label: 'Upcoming', value: '2', tone: 'available' },
                    { icon: '✅', label: 'Completed', value: '4', tone: 'pending' },
                    { icon: '🎟️', label: 'Total places', value: '36', tone: 'available' },
                    { icon: '🧾', label: 'Generic waitlist', value: 'Open', tone: 'available' },
                  ], {
                    actions: ['New course'],
                  }),
                  listCard('Courses', [
                    { title: 'May Evening Course', meta: '6 sessions • Capacity 18 • Open to all', badge: 'Live' },
                    { title: 'June Weekend Course', meta: '4 sessions • Capacity 12 • Waitlist only', badge: 'Live' },
                  ]),
                ],
              },
              {
                key: 'ltr-signup',
                title: 'Prospects sign up through the course join flow',
                description:
                  'Course signup is part of the real join journey, not a separate marketing form.',
                cards: [
                  joinFormCard('Learn to Row Signup', [
                    'Choose your course',
                    'Emergency contact',
                    'Can you swim 50m?',
                    'Previous rowing experience',
                  ], {
                    enabledLabel:
                      'The signup flow uses the same question model as the wider join system and can feed profile fields later.',
                  }),
                  paymentCard('Course Payment', [
                    { label: 'Learn to Row course fee', value: '£95.00' },
                    { label: 'Platform fee', value: '£1.00' },
                    { label: 'Stripe fee', value: '£2.88' },
                    { label: 'Total', value: '£99.38', total: true },
                  ]),
                ],
              },
              {
                key: 'ltr-waitlist',
                title: 'Full courses can move people onto a waitlist and release them later',
                description:
                  'Where capacity is full, Learn to Row can use a release flow rather than losing the prospect entirely.',
                cards: [
                  listCard('Learn to Row Waitlist', [
                    { title: 'Lucy Green', meta: 'May Evening Course • waiting for a released place' },
                    { title: 'Tom Hall', meta: 'June Weekend Course • waiting for a released place' },
                  ]),
                  notificationsCard('Recent Notifications', [
                    { message: 'A Learn to Row place is now available for May Evening Course.', meta: 'Today at 09:20' },
                  ]),
                ],
              },
              {
                key: 'ltr-manage',
                title: 'Admins keep managing courses from the same module',
                description:
                  'After signup starts, the course card remains the place to review live and completed cohorts.',
                cards: [
                  listCard('Courses', [
                    { title: 'May Evening Course', meta: '6 sessions • 16/18 booked • 2 on waitlist', badge: 'Live' },
                    { title: 'March Weekend Course', meta: '4 sessions • completed • conversion follow-up sent', badge: 'Completed' },
                  ]),
                ],
              },
              {
                key: 'ltr-events',
                title: 'Generated sessions feed into the wider events/calendar surfaces',
                description:
                  'Course sessions can become standard events, which means members then see them in the normal event and calendar places too.',
                cards: [
                  eventListCard('Events', [
                    { title: 'May Evening Course - Session 1', meta: 'Tue 6 May 2026 • 18:30 • Boathouse' },
                    { title: 'May Evening Course - Session 2', meta: 'Thu 8 May 2026 • 18:30 • Boathouse' },
                  ]),
                  dashboardCard('Your Upcoming Events', [
                    { title: 'May Evening Course - Session 1', meta: '06/05/2026 @ 18:30 • Boathouse' },
                    { title: 'May Evening Course - Session 2', meta: '08/05/2026 @ 18:30 • Boathouse' },
                  ], {
                    buttonLabel: 'View Full Calendar',
                  }),
                ],
              },
            ],
          },
          {
            key: 'boatMaintenance',
            label: 'Boat Maintenance',
            description:
              'Track rowing boat maintenance tickets, member reports, updates, and out-of-service state.',
            highlights: [
              'Maintenance ticket creation',
              'Member issue reporting controls',
              'Status filters and updates',
              'Rowing fleet out-of-order tracking',
            ],
            stages: [
              {
                key: 'rowing-maintenance-summary',
                title: 'Admins track open and untracked boat issues',
                description:
                  'The maintenance card keeps open rowing boat issues, untracked out-of-order boats, and status filters in one operational view.',
                cards: [
                  statusCard('Boat Maintenance', [
                    { icon: '🛠️', label: 'Open', value: '5', tone: 'unavailable' },
                    { icon: '⚠️', label: 'Untracked', value: '2', tone: 'pending' },
                  ], {
                    actions: ['Create ticket'],
                  }),
                  listCard('Open tickets', [
                    { title: 'Resolute 2x rigger check', meta: 'Rowing • Resolute 2x • updated today', badge: 'Open' },
                    { title: 'Swift single seat runner', meta: 'Rowing • Swift single • waiting', badge: 'Waiting' },
                    { title: 'Eight trailer light repair', meta: 'Rowing • Trailer • in progress', badge: 'In progress' },
                  ]),
                ],
              },
              {
                key: 'rowing-maintenance-member',
                title: 'Members can report rowing boat issues if enabled',
                description:
                  'Clubs can choose whether rowing members can report issues and whether existing ticket progress is visible to them.',
                cards: [
                  listCard('Report Boat Issue', [
                    { title: 'Boat', meta: 'Choose from member-visible rowing boats', badge: 'Required' },
                    { title: 'Reason', meta: 'e.g. Seat runner sticking', badge: 'Required' },
                    { title: 'Details', meta: 'Repair notes, parts needed, or next step', badge: 'Optional' },
                  ]),
                  notificationsCard('Recent Notifications', [
                    { message: 'Maintenance ticket created for Resolute 2x.', meta: 'Today at 13:06' },
                    { message: 'Swift single seat runner was marked in progress.', meta: 'Yesterday at 15:42' },
                  ]),
                ],
              },
              {
                key: 'rowing-maintenance-updates',
                title: 'Ticket updates keep the repair history attached',
                description:
                  'Admins can post repair notes, change ticket status, and reopen resolved work when a problem returns.',
                cards: [
                  listCard('Ticket Updates', [
                    { title: 'Ticket created', meta: 'Captain • today @ 09:12', badge: 'Created' },
                    { title: 'Status: In progress', meta: 'Boatman • today @ 10:05', badge: 'Update' },
                    { title: 'Parts ordered', meta: 'Replacement runner expected Friday', badge: 'Note' },
                  ]),
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    key: 'sailing',
    label: 'Sailing Features',
    description:
      'Sailing-specific modules for training, duties, moorings, fleet operations, crew coordination, and maintenance.',
    groups: [
      {
        key: 'sailing',
        title: 'Sailing Clubs',
        description: 'Specialist workflows for club sailing operations.',
        modules: [
          {
            key: 'learnToSail',
            label: 'Learn to Sail',
            description:
              'Run beginner sailing courses with capped places, waitlists, payments, generated sessions, and post-course follow-up.',
            highlights: [
              'Course setup and capacity management',
              'Signup and course payment flow',
              'Waitlist release handling',
              'Generated event and calendar sessions',
            ],
            stages: [
              {
                key: 'lts-setup',
                title: 'Create and manage sailing courses',
                description:
                  'Admins keep upcoming courses, waitlist mode, fee handling, and conversion settings together in one module card.',
                cards: [
                  statusCard('Learn to Sail', [
                    { icon: '⛵', label: 'Upcoming', value: '3', tone: 'available' },
                    { icon: '✅', label: 'Completed', value: '5', tone: 'pending' },
                    { icon: '🎟️', label: 'Total places', value: '42', tone: 'available' },
                    { icon: '🧾', label: 'Generic waitlist', value: 'Open', tone: 'available' },
                    { icon: '💸', label: 'Fee handling', value: 'MN add · Stripe add', tone: 'available' },
                  ], {
                    actions: ['New course'],
                  }),
                  listCard('Courses', [
                    { title: 'May Start Sailing', meta: '6 sessions • Capacity 16 • Open to all', badge: 'Live' },
                    { title: 'June Weekend Dinghy Course', meta: '4 sessions • Capacity 12 • Waitlist only', badge: 'Live' },
                  ]),
                ],
              },
              {
                key: 'lts-signup',
                title: 'Prospects sign up and pay through the course journey',
                description:
                  'Learn to Sail uses the same join and checkout patterns as the rest of the platform, so course signup is not a separate system.',
                cards: [
                  joinFormCard('Learn to Sail Signup', [
                    'Choose your course',
                    'Emergency contact',
                    'Can you swim 50m?',
                    'Previous sailing experience',
                  ], {
                    enabledLabel:
                      'Course signup can collect the club-specific details needed before participants arrive.',
                  }),
                  paymentCard('Course Payment', [
                    { label: 'Learn to Sail course fee', value: '£125.00' },
                    { label: 'Platform fee', value: '£1.00' },
                    { label: 'Stripe fee', value: '£3.68' },
                    { label: 'Total', value: '£130.18', total: true },
                  ]),
                ],
              },
              {
                key: 'lts-waitlist',
                title: 'Full courses can release places later',
                description:
                  'When a course is full, prospects can wait for a released place and continue into payment when an offer is made.',
                cards: [
                  listCard('Learn to Sail Waitlist', [
                    { title: 'Nina Roberts', meta: 'May Start Sailing • waiting for a released place' },
                    { title: 'Jamie Clarke', meta: 'June Weekend Dinghy Course • waiting for a released place' },
                  ]),
                  notificationsCard('Recent Notifications', [
                    { message: 'A Learn to Sail place is now available for May Start Sailing.', meta: 'Today at 09:20' },
                  ]),
                ],
              },
              {
                key: 'lts-events',
                title: 'Course sessions feed into events and calendars',
                description:
                  'Generated course sessions can appear as normal events, keeping member calendars and club schedules aligned.',
                cards: [
                  eventListCard('Events', [
                    { title: 'May Start Sailing - Session 1', meta: 'Tue 5 May 2026 • 18:30 • Training pontoon' },
                    { title: 'May Start Sailing - Session 2', meta: 'Thu 7 May 2026 • 18:30 • Training pontoon' },
                  ]),
                  dashboardCard('Your Upcoming Events', [
                    { title: 'May Start Sailing - Session 1', meta: '05/05/2026 @ 18:30 • Training pontoon' },
                    { title: 'May Start Sailing - Session 2', meta: '07/05/2026 @ 18:30 • Training pontoon' },
                  ], {
                    buttonLabel: 'View Full Calendar',
                  }),
                ],
              },
            ],
          },
          {
            key: 'dutyRoster',
            label: 'Duty Roster',
            description:
              'Plan sailing duties from event roles, track member responses, and manage swaps across the season.',
            highlights: [
              'Duty slots generated from event roles',
              'Member response tracking',
              'Swap requests',
              'Admin roster issue summary',
            ],
            stages: [
              {
                key: 'duty-admin',
                title: 'Admins see the season roster at a glance',
                description:
                  'The roster card highlights open issues, pending replies, and swaps so duty management does not get buried in event setup.',
                cards: [
                  dutyRosterCard('Duty Roster', {
                    eyebrow: 'Roster overview',
                    titleText: '4 roster issues',
                    meta: '38 upcoming duty slots',
                    action: 'Open roster',
                    stats: [
                      { label: 'Roster issues', value: '4', tone: 'danger' },
                      { label: 'Pending replies', value: '7', tone: 'warn' },
                      { label: 'Open swaps', value: '2', tone: 'danger' },
                    ],
                  }),
                  eventListCard('Events', [
                    { title: 'Club Racing Night', meta: 'Thu 30 Apr 2026 • Safety boat, race officer, galley roles' },
                    { title: 'Junior Training', meta: 'Sat 2 May 2026 • Instructor support and shore crew' },
                  ]),
                ],
              },
              {
                key: 'duty-member',
                title: 'Members get a clear view of their own duties',
                description:
                  'Assigned members can see the next duty, respond when needed, and track swap options from the same module.',
                cards: [
                  dutyRosterCard('Duty Roster', {
                    eyebrow: 'Response needed',
                    titleText: '1 duty needs your reply',
                    meta: 'Club Racing Night • Safety boat crew',
                    action: 'Review duties',
                    stats: [
                      { label: 'My duties', value: '3', tone: 'ok' },
                      { label: 'Responses', value: '1', tone: 'danger' },
                      { label: 'Swaps', value: '1', tone: 'warn' },
                    ],
                  }),
                  notificationsCard('Recent Notifications', [
                    { message: 'Please confirm your safety boat duty for Club Racing Night.', meta: 'Today at 08:10' },
                    { message: 'A duty swap request is available for Saturday junior training.', meta: 'Yesterday at 17:45' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'moorings',
            label: 'Moorings',
            description:
              'Manage mooring maps, allocations, waiting lists, maintenance blocks, and linked mooring fee payments.',
            highlights: [
              'Admin allocation and availability summary',
              'Member-safe mooring map',
              'Waitlist requests and offers',
              'Linked mooring fee payment status',
            ],
            stages: [
              {
                key: 'moorings-admin',
                title: 'Admins manage allocations, availability, and fees',
                description:
                  'The moorings card keeps occupancy, unpaid allocations, waitlist demand, and maintenance blocks visible from the main club view.',
                cards: [
                  statusCard('Moorings', [
                    { icon: '⚓', label: 'Occupied', value: '28', tone: 'available' },
                    { icon: '🟢', label: 'Vacant', value: '6', tone: 'available' },
                    { icon: '💸', label: 'Unpaid', value: '3', tone: 'pending' },
                    { icon: '🛠️', label: 'Maintenance', value: '2', tone: 'unavailable' },
                  ], {
                    paragraph:
                      'Admins can jump into allocations, waitlist offers, maintenance notes, and linked mooring fee bolt-ons.',
                    actions: ['Manage'],
                  }),
                  listCard('Mooring Waitlist', [
                    { title: 'Maya Collins', meta: 'General request • 6.2m dayboat', badge: 'Waiting' },
                    { title: 'Tom Hall', meta: 'Requested A12 • 5.8m dinghy', badge: 'Offered' },
                    { title: 'Priya Shah', meta: 'General request • 7.0m cruiser', badge: 'Waiting' },
                  ]),
                ],
              },
              {
                key: 'moorings-map',
                title: 'Members can see a safe availability map',
                description:
                  'Clubs can show availability and member-safe details while keeping private allocation information out of the public member view.',
                cards: [
                  mooringMapCard('Mooring Map', {
                    area: 'North Pontoon',
                    summary: [
                      { label: 'Vacant', value: '6' },
                      { label: 'Reserved', value: '2' },
                      { label: 'Areas', value: '3' },
                    ],
                    spaces: [
                      { label: 'A1', status: 'occupied' },
                      { label: 'A2', status: 'mine' },
                      { label: 'A3', status: 'vacant' },
                      { label: 'A4', status: 'reserved' },
                      { label: 'B1', status: 'maintenance' },
                      { label: 'B2', status: 'vacant' },
                    ],
                    detailTitle: 'A3',
                    detailRows: ['Vacant', 'Max length 6.5m', 'Types: dinghy, dayboat'],
                  }),
                ],
              },
              {
                key: 'moorings-waitlist',
                title: 'Waitlist offers can move into payment',
                description:
                  'When a space is offered, the member can accept and pay through the linked payment path before the allocation becomes active.',
                cards: [
                  statusCard('My Mooring', [
                    { icon: '⚓', label: 'Status', value: 'Offered', tone: 'pending' },
                    { icon: '📍', label: 'Space', value: 'A3', tone: 'available' },
                  ], {
                    paragraph: 'North Pontoon • Daydream • offered 22 Apr 2026',
                    actions: ['Accept and pay'],
                  }),
                  paymentCard('Mooring Payment', [
                    { label: 'Annual mooring fee', value: '£240.00' },
                    { label: 'Platform fee', value: '£1.00' },
                    { label: 'Stripe fee', value: '£7.25' },
                    { label: 'Total', value: '£248.75', total: true },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'clubBoats',
            label: 'Club Boats',
            description:
              'Track club-owned sailing boats, statuses, sail numbers, mooring links, booking availability, and maintenance state.',
            highlights: [
              'Fleet list and active/maintenance counts',
              'Sail number and mooring link tracking',
              'Maintenance handoff',
              'Member-safe boat labels where needed',
            ],
            stages: [
              {
                key: 'sailing-boats-admin',
                title: 'Admins keep the sailing fleet organised',
                description:
                  'Club Boats shows active boats, maintenance state, mooring links, and quick maintenance handoff for admin users.',
                cards: [
                  statusCard('Club Boats', [
                    { icon: '⛵', label: 'Active', value: '12', tone: 'available' },
                    { icon: '🛠️', label: 'Maintenance', value: '2', tone: 'unavailable' },
                    { icon: '📋', label: 'Total', value: '15', tone: 'pending' },
                  ]),
                  listCard('Fleet', [
                    { title: 'Puffin', meta: 'Sail 214 • 4.2m • Mooring linked', badge: 'Active' },
                    { title: 'Sandpiper', meta: 'Sail 382 • 5.1m • Mooring linked', badge: 'Active' },
                    { title: 'Committee Launch', meta: 'RIB • out of order', badge: 'Maintenance' },
                  ]),
                ],
              },
              {
                key: 'sailing-boats-maintenance',
                title: 'Fleet issues can become maintenance tickets',
                description:
                  'When a boat needs attention, admins can send it into the shared boat maintenance workflow without losing the fleet context.',
                cards: [
                  listCard('Maintenance handoff', [
                    { title: 'Committee Launch', meta: 'Engine service required • ticket created', badge: 'In progress' },
                    { title: 'Puffin', meta: 'Jib sheet replacement • reported by member', badge: 'Open' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'sailingBoatBooking',
            label: 'Sailing Boat Booking',
            description:
              'Let members request club boats by time slot and manage the shared sailing allocation flow.',
            highlights: [
              'Member club boat request flow',
              'Active fleet and allocation day summary',
              'Preference and allocation outcomes',
              'Request cancellation and notifications',
            ],
            stages: [
              {
                key: 'sailing-booking-request',
                title: 'Members request a club boat for a sailing slot',
                description:
                  'Members choose a date, time range, and preferred boats, then wait for the allocation outcome.',
                cards: [
                  statusCard('Sailing Boat Booking', [
                    { icon: '⛵', label: 'Active boats', value: '12', tone: 'available' },
                    { icon: '📋', label: 'Your requests', value: '2', tone: 'pending' },
                    { icon: '📅', label: 'Allocation day', value: 'Mon', tone: 'available' },
                  ], {
                    actions: ['Request a club boat'],
                  }),
                  listCard('Your upcoming', [
                    { title: 'Sat 26 Apr • 09:00-11:00', meta: 'Prefs: Puffin, Sandpiper', badge: 'Pending' },
                    { title: 'Sun 27 Apr • 14:00-16:00', meta: 'Puffin allocated', badge: 'Allocated' },
                  ]),
                ],
              },
              {
                key: 'sailing-booking-outcome',
                title: 'Allocation outcomes stay visible',
                description:
                  'After allocation, requests can show the selected boat, pending state, cancellation, or unfilled outcome.',
                cards: [
                  listCard('Allocation outcome', [
                    { title: 'Sun 27 Apr • Maya Collins', meta: 'Puffin allocated', badge: 'Allocated' },
                    { title: 'Sun 27 Apr • Tom Hall', meta: 'No suitable club boat free', badge: 'Unfilled' },
                    { title: 'Mon 28 Apr • Alex Turner', meta: 'Awaiting allocation', badge: 'Pending' },
                  ]),
                  notificationsCard('Recent Notifications', [
                    { message: 'Your sailing boat request for Sun 27 Apr has been allocated to Puffin.', meta: 'Today at 06:12' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'sailingOutings',
            label: 'Club-led Outings',
            description:
              'Build club-led sailing sessions, assign crews, allocate boats, and link outings back to events.',
            highlights: [
              'Admin outing drafts and submissions',
              'Crew assignment and boat allocation',
              'Member My Club-led Outings card',
              'Event and calendar linkage',
            ],
            stages: [
              {
                key: 'sailing-outings-admin',
                title: 'Admins draft and submit club-led outings',
                description:
                  'Club-led Outings depends on Events and Sailing Boat Booking, then brings crew planning and boat allocation into one workflow.',
                cards: [
                  statusCard('Club-led Outings', [
                    { icon: '🧭', label: 'Drafts', value: '2', tone: 'pending' },
                    { icon: '✅', label: 'Submitted', value: '4', tone: 'available' },
                    { icon: '📅', label: 'Upcoming', value: '5', tone: 'available' },
                  ], {
                    actions: ['New outing'],
                  }),
                  listCard('Upcoming outings', [
                    { title: 'Saturday harbour sail', meta: 'Sat 26 Apr • 3 crews • submitted', badge: 'Submitted' },
                    { title: 'Sunday novice sail', meta: 'Sun 27 Apr • 2 crews • draft', badge: 'Draft' },
                  ]),
                ],
              },
              {
                key: 'sailing-outings-member',
                title: 'Assigned members see their club-led outings',
                description:
                  'Once submitted, members can see where they have been assigned and open the linked event when there is one.',
                cards: [
                  listCard('My Club-led Outings', [
                    { title: 'Saturday harbour sail', meta: 'Sat 26 Apr • Boat: Sandpiper', badge: 'Upcoming' },
                    { title: 'Sunday novice sail', meta: 'Sun 27 Apr • allocation pending', badge: 'Pending' },
                  ]),
                  dashboardCard('Your Upcoming Events', [
                    { title: 'Saturday harbour sail', meta: '26/04/2026 @ 09:00 • Harbour' },
                  ], {
                    buttonLabel: 'View Full Calendar',
                  }),
                ],
              },
              {
                key: 'sailing-outings-notify',
                title: 'Assignments and allocation updates can notify members',
                description:
                  'Notifications help members understand when they have been assigned or when a boat outcome changes.',
                cards: [
                  notificationsCard('Recent Notifications', [
                    { message: 'You have been assigned to Saturday harbour sail.', meta: 'Today at 07:05' },
                    { message: 'Boat allocation for Sunday novice sail is still pending.', meta: 'Yesterday at 18:34' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'crewFinder',
            label: 'Crew Finder',
            description:
              'Help skippers find crew and members share availability using club boats, moorings, and events.',
            highlights: [
              'Crew needed posts',
              'Available-to-crew posts',
              'Club-controlled posting permissions',
              'Member and admin visibility from the team card',
            ],
            stages: [
              {
                key: 'crew-finder-summary',
                title: 'The team card shows current crew demand',
                description:
                  'Members can see open crew-needed and available-to-crew posts, while clubs control who can post.',
                cards: [
                  statusCard('Crew Finder', [
                    { icon: '🙋', label: 'Crew needed', value: '5', tone: 'available' },
                    { icon: '⛵', label: 'Available crew', value: '3', tone: 'pending' },
                    { icon: '📌', label: 'Your posts', value: '1', tone: 'unavailable' },
                  ], {
                    actions: ['Request crew', 'Share availability'],
                  }),
                  listCard('Open Crew Finder Posts', [
                    { title: 'Need one for Saturday harbour sail', meta: 'Crew needed • Sandpiper • Maya Collins', badge: 'Open' },
                    { title: 'Available Sunday afternoon', meta: 'Available to crew • experienced helm', badge: 'Open' },
                    { title: 'Looking for novice practice crew', meta: 'Crew needed • Puffin', badge: 'Open' },
                  ]),
                ],
              },
              {
                key: 'crew-finder-settings',
                title: 'Posting rules can match the club’s operating model',
                description:
                  'Admins can limit posting to admins, all members, or tagged members, and can separately allow crew-needed or availability posts.',
                cards: [
                  listCard('Crew Finder settings', [
                    { title: 'Posting mode', meta: 'Tagged members can post', badge: 'Configured' },
                    { title: 'Crew-needed posts', meta: 'Allowed', badge: 'On' },
                    { title: 'Availability posts', meta: 'Allowed', badge: 'On' },
                  ]),
                ],
              },
              {
                key: 'crew-finder-notifications',
                title: 'Relevant members can be notified about new posts',
                description:
                  'Crew Finder notifications help sailing members notice open opportunities without checking the board manually.',
                cards: [
                  notificationsCard('Recent Notifications', [
                    { message: 'New Crew Finder post: Need one for Saturday harbour sail.', meta: 'Today at 12:35' },
                    { message: 'A member is available to crew on Sunday afternoon.', meta: 'Yesterday at 17:18' },
                  ]),
                ],
              },
            ],
          },
          {
            key: 'boatMaintenance',
            label: 'Boat Maintenance',
            description:
              'Track sailing boat maintenance tickets, member reports, updates, and out-of-service state.',
            highlights: [
              'Maintenance ticket creation',
              'Member issue reporting controls',
              'Status filters and updates',
              'Sailing fleet out-of-order tracking',
            ],
            stages: [
              {
                key: 'sailing-maintenance-summary',
                title: 'Admins track open and untracked boat issues',
                description:
                  'The maintenance card brings sailing and rowing into the same ticket model while still respecting sport-specific permissions.',
                cards: [
                  statusCard('Boat Maintenance', [
                    { icon: '🛠️', label: 'Open', value: '4', tone: 'unavailable' },
                    { icon: '⚠️', label: 'Untracked', value: '1', tone: 'pending' },
                  ], {
                    actions: ['Create ticket'],
                  }),
                  listCard('Open tickets', [
                    { title: 'Puffin jib sheet replacement', meta: 'Sailing • Puffin • updated today', badge: 'Open' },
                    { title: 'Committee Launch engine service', meta: 'Sailing • Committee Launch • waiting', badge: 'Waiting' },
                    { title: 'Sandpiper rudder inspection', meta: 'Sailing • Sandpiper • in progress', badge: 'In progress' },
                  ]),
                ],
              },
              {
                key: 'sailing-maintenance-member',
                title: 'Members can report issues if the club allows it',
                description:
                  'Clubs can choose whether sailing members can report issues and whether they can see ticket progress afterwards.',
                cards: [
                  listCard('Report Boat Issue', [
                    { title: 'Boat', meta: 'Choose from member-visible sailing boats', badge: 'Required' },
                    { title: 'Reason', meta: 'e.g. Rudder damage', badge: 'Required' },
                    { title: 'Details', meta: 'Repair notes, parts needed, or next step', badge: 'Optional' },
                  ]),
                  notificationsCard('Recent Notifications', [
                    { message: 'Maintenance ticket created for Puffin.', meta: 'Today at 13:06' },
                    { message: 'Committee Launch engine service was marked in progress.', meta: 'Yesterday at 15:42' },
                  ]),
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export { featureFamilies };

export const generalModuleGroups = featureFamilies.find((family) => family.key === 'general')?.groups || [];
export const rowingModuleGroups = featureFamilies.find((family) => family.key === 'rowing')?.groups || [];
export const sailingModuleGroups = featureFamilies.find((family) => family.key === 'sailing')?.groups || [];

export const rowingDependencies = [
  'Events',
  'Payments',
  'Messaging',
  'Member records',
];

export const homeOverviewGroups = [
  {
    title: 'Memberships and payments',
    count: 'Core workflows',
    description: 'Set up membership options, take payments, handle discounts, and keep everything connected.',
  },
  {
    title: 'Joining and member experience',
    count: 'Member-facing',
    description: 'Give new members a clear way to join, pay, message the club, and stay up to date.',
  },
  {
    title: 'Events and communication',
    count: 'Shared data',
    description: 'Keep events, messages, notifications, and member information working together instead of split across tools.',
  },
];

export const homePlatformPoints = [
  {
    title: 'It cuts down admin',
    description:
      'Membership setup, join forms, payments, and communication all live in the same system.',
  },
  {
    title: 'It makes joining clearer',
    description:
      'New members can move from joining to paying to receiving updates without being pushed between separate tools.',
  },
  {
    title: 'It keeps members better informed',
    description:
      'Messages, documents, events, and notifications stay connected, so members can find what they need more easily.',
  },
];
